// 📐 useViewport — THE viewport answer (08-03, mobile-arc round 5).
// Before this hook the app had five separate detection mechanisms across
// three thresholds (GameStepPage state+listener, per-render one-shot reads,
// VotingModal/HumanInputForm private listeners, InvestigationStage
// matchMedia), so a 750px window could be "mobile" to one component and
// "desktop" to its sibling in the same frame. One store, one listener,
// one set of breakpoints.
//
// Semantics are EXACT matches for what the migrated sites used — do not
// "improve" a threshold here without a screen-size audit (standing rule:
// container-width beats viewport-width where a component has a natural
// container; this hook is for the places where the viewport IS the
// container).
import { useSyncExternalStore } from 'react';

export const BP_MOBILE = 768;   // width <  : phone layouts
export const BP_NARROW = 1100;  // width <  : narrow desktop (scrolling tab strips, case-file modal)
export const SHORT_H = 700;     // height < : short viewport (compact modals)

// 📏 The fixed bottom-nav band on phones. 53 was hand-typed at ~11 sites
// (48 at one of them was the 07-16 seam bug) — import these instead.
// Comp phones add COMP_TICKER_H (the phase ticker rides the nav top);
// that constant lives in GameStepPage with the ticker itself.
export const NAV_H = 53;
export const NAV_BOTTOM = `calc(${NAV_H}px + env(safe-area-inset-bottom, 0px))`;
// Anchor helper for band residents that float above the nav by a margin.
export const navBottomPlus = (px) => `calc(${NAV_H}px + env(safe-area-inset-bottom, 0px) + ${px}px)`;

const compute = () => {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const h = typeof window !== 'undefined' ? window.innerHeight : 900;
  return {
    width: w,
    height: h,
    isMobile: w < BP_MOBILE,
    // NOTE: matches the historical GameStepPage semantics — TRUE for phones
    // too (every consumer pairs it with isMobile). Not "between 768-1100".
    isNarrowDesktop: w < BP_NARROW,
    isShort: h < SHORT_H,
  };
};

let snap = compute();
const subs = new Set();
const onResize = () => {
  const next = compute();
  if (next.width === snap.width && next.height === snap.height) return;
  snap = next;
  subs.forEach((fn) => fn());
};
// ⚠️ The listener attaches at IMPORT, not on first subscribe (sweep #46 RCA):
// module eval runs before the viewport settles (headless emulation applies
// its metrics ~140ms after doc-start; real browsers have the same class of
// early-eval races), and a resize fired BEFORE the first React subscriber
// used to be missed forever — the store froze on the import-time width and
// the app rendered a desktop/mobile chimera at 375px (composer unmountable,
// send-scroll gate red). One permanent listener on a singleton store is the
// fix, plus a recompute at subscribe time so the first component MOUNT heals
// any earlier misread — mount-time reads are exactly the semantics every
// migrated site had before round 5.
if (typeof window !== 'undefined') {
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);
}
const subscribe = (fn) => {
  onResize();
  subs.add(fn);
  return () => subs.delete(fn);
};
const getSnapshot = () => snap;

export default function useViewport() {
  return useSyncExternalStore(subscribe, getSnapshot);
}
